import React, { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';

import { FamilyMember } from './components';
import { FieldWithValueDto } from 'theSource/types';
import { DependentDto } from 'theSource/features/FamilyMembers/types';
import { getDependentAge, isDependentDisabled } from './utils';
import { DrawerDialog } from 'enrollment/components/DrawerDialog';

interface FamilySelectProps {
  dependents?: DependentDto[];
  selectedDependentIds: number[];
  dependentsOnCoverage: number[];
  subscriberProfile?: FieldWithValueDto;
  canIncrease?: boolean;
  canDecrease?: boolean;
  isDependentOnlyPlan: boolean;
  requiredDependents?: number[];
  eligibleDependents?: number[];
  hideCheckBox: boolean;
  onChange?: (id?: number) => void;
}

export const FamilySelect = ({
  dependents,
  selectedDependentIds,
  dependentsOnCoverage,
  subscriberProfile,
  requiredDependents,
  eligibleDependents,
  canIncrease = true,
  canDecrease = true,
  isDependentOnlyPlan = false,
  hideCheckBox = false,
  onChange,
}: FamilySelectProps) => {
  const { t } = useTranslation('pages.familySelect');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const handleLinkClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setDrawerOpen(true);
  };

  if (!dependents || dependents.length === 0) {
    return <></>;
  }

  const maxDependentsToShow = isMobile ? 2 : dependents.length;
  const subscriberFamilyCoveredValue = isDependentOnlyPlan ? 0 : 1;
  const familyCoveredCount = selectedDependentIds?.length
    ? selectedDependentIds.length
    : 0;

  return (
    <fieldset className="mt-4">
      <legend className="mb-2" data-testid="family-covered">
        <h2 className="headline-3">
          {t('familyCovered')} (
          {familyCoveredCount + subscriberFamilyCoveredValue})
        </h2>
      </legend>
      {isDependentOnlyPlan ? null : (
        <FamilyMember isYou={true} profile={subscriberProfile} />
      )}
      {dependents.slice(0, maxDependentsToShow).map((dependent) => {
        const notEligibleDependents =
          eligibleDependents &&
          !eligibleDependents?.includes(dependent.id) &&
          !requiredDependents?.includes(dependent.id);

        return (
          <Fragment key={dependent.id}>
            {!notEligibleDependents && (
              <FamilyMember
                id={dependent.id}
                name={dependent.first_name}
                age={getDependentAge(dependent.birthdate)}
                profile={dependent.dependent_profile}
                onChange={onChange}
                selected={selectedDependentIds.includes(dependent.id)}
                hideCheckbox={hideCheckBox}
                qmcso={String(dependent.qmcso || '') === 'true'}
                disabled={isDependentDisabled({
                  dependentId: dependent.id,
                  dependentsOnCoverage,
                  canIncrease,
                  canDecrease,
                  eligibleDependents,
                  requiredDependents,
                })}
              />
            )}
          </Fragment>
        );
      })}
      {dependents.length > maxDependentsToShow && isMobile && (
        <button
          onClick={handleLinkClick}
          className="faux-link body2"
          id="view_covered"
          data-testid="family_more_link"
        >{`+ ${dependents.length - maxDependentsToShow} More`}</button>
      )}
      {/* Add the DrawerDialog component */}
      {isMobile && (
        <DrawerDialog
          id="family-select-drawer"
          openDialog={isDrawerOpen}
          closeDialog={() => setDrawerOpen(false)}
          dialogHeading={t('familyCovered')}
          okButtonLabel={t('common:save')}
          closeButtonLabel={t('common:cancel')}
          hideFooter
        >
          <>
            {isDependentOnlyPlan ? null : (
              <FamilyMember isYou={true} profile={subscriberProfile} />
            )}
            {dependents.map((dependent, index) => {
              const notEligibleDependents =
                eligibleDependents &&
                !eligibleDependents?.includes(dependent.id);
              return (
                <Fragment key={index}>
                  {!notEligibleDependents && (
                    <FamilyMember
                      id={dependent.id}
                      name={dependent.first_name}
                      age={getDependentAge(dependent.birthdate)}
                      profile={dependent.dependent_profile}
                      onChange={onChange}
                      selected={selectedDependentIds.includes(dependent.id)}
                      key={dependent.id}
                      disabled={isDependentDisabled({
                        dependentId: dependent.id,
                        dependentsOnCoverage,
                        canIncrease,
                        canDecrease,
                        eligibleDependents,
                        requiredDependents,
                      })}
                    />
                  )}
                </Fragment>
              );
            })}
          </>
        </DrawerDialog>
      )}
    </fieldset>
  );
};
